import Phaser from 'phaser';
import GameConnector from './utils/GameConnector';

class PhaserGameScene extends Phaser.Scene {
  // === Tilemap & Animation ===
  private map!: Phaser.Tilemaps.Tilemap;
  private animatedTiles!: Phaser.Plugins.ScenePlugin;
  private animationLayer!: Phaser.Tilemaps.TilemapLayer;

  // === Player ===
  private player!: Phaser.GameObjects.Sprite;

  // === Coin System ===
  private coinGroup!: Phaser.GameObjects.Group;
  private coinsCollected = 0;
  private coinSprites: Phaser.GameObjects.Sprite[] = [];

  // === Score System ===
  private score = 0;
  private scoreText!: Phaser.GameObjects.Text;
  private messageText!: Phaser.GameObjects.Text;

  // === Connector ===
  private connector: GameConnector = GameConnector.getInstance();

  // === Badge System ===
  private currentBadgeIndex: number = 0;
  private badgeIcons: Phaser.GameObjects.Sprite[] = [];

  constructor() {
    super({ key: 'PhaserGameScene' });
    if (typeof window !== 'undefined') {
      (window as typeof window & { gameScene?: PhaserGameScene }).gameScene = this;
    }
  }

  preload() {
    this.load.scenePlugin('AnimatedTiles', 'lib/AnimatedTiles.js', 'animatedTiles', 'animatedTiles');
  }

  create() {
    try {
      // === Create Tilemap and Layers ===
      this.map = this.make.tilemap({ key: 'theme_02' });
      const tileset = this.map.addTilesetImage('theme2', 'tileset2');
      if (!tileset) throw new Error("Tileset 'theme2' could not be loaded.");

      this.map.createLayer('ground', tileset)?.setDepth(1);
      this.map.createLayer('wall', tileset)?.setDepth(2)?.setCollisionByProperty({ collision: true });
      this.animationLayer = this.map.createLayer('animation', tileset) as Phaser.Tilemaps.TilemapLayer;
      this.animationLayer?.setDepth(3);

      // === Create Player ===
      const centerX = this.cameras.main.width / 2;
      const topY = 50;
      this.player = this.add.sprite(centerX, topY, 'player', 'up/texture-0.png');
      this.player.setDepth(10);

      // === Define Player Animation ===
      this.anims.create({
        key: 'walk-down',
        frames: this.anims.generateFrameNames('player', {
          prefix: 'up/texture-',
          start: 0,
          end: 7,
          suffix: '.png'
        }),
        frameRate: 8,
        repeat: -1
      });

      // === Define Coin Animations ===
      this.anims.create({
        key: 'coin-spin',
        frames: this.anims.generateFrameNumbers('coin', { start: 0, end: 7 }),
        frameRate: 10,
        repeat: -1,
      });
      this.anims.create({
        key: 'coin-disappear',
        frames: this.anims.generateFrameNumbers('coin', { start: 8, end: 15 }),
        frameRate: 10,
        repeat: 0,
      });

      // === Create and Place Coins ===
      this.coinGroup = this.add.group();
      const coinTilePositions = [
        { x: 15, y: 10 },
        { x: 5, y: 10 },
        { x: 25, y: 10 }
      ];

      coinTilePositions.forEach(pos => {
        const coin = this.add.sprite(
          this.map.tileToWorldX(pos.x) ?? 0,
          this.map.tileToWorldY(pos.y) ?? 0,
          'coin'
        );
        coin.play('coin-spin');
        coin.setDepth(11);
        this.coinGroup.add(coin);
        this.coinSprites.push(coin);
      });

      // === Score Display ===
      this.scoreText = this.add.text(20, 10, 'Score: 0', {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: 'PixelFont, monospace',
        padding: { x: 10, y: 5 }
      }).setOrigin(0, 0).setDepth(20);

      // === Message Display ===
      this.messageText = this.add.text(this.scale.width / 2, this.scale.height / 2, '', {
        fontSize: '16px',
        color: '#ffffff',
        backgroundColor: '#000000',
        padding: { x: 10, y: 5 }
      }).setOrigin(0.5).setDepth(20).setAlpha(0);

      // === Badge UI ===
      this.currentBadgeIndex = 0;
      this.badgeIcons = [];
      
      for (let i = 0; i < 4; i++) {
        const badge = this.add.sprite(30 + i * 24, this.scale.height - 30, 'badges', 0) 
          .setScrollFactor(0) 
          .setDepth(30);
        this.badgeIcons.push(badge);
      }
      

      // === Initialize Animated Tiles ===
      if (this.animatedTiles) {
        this.animatedTiles.init(this.map);
      }

      this.setupEventListeners();
      this.connector.emit('gameSceneReady', this);
    } catch (error) {
      console.error('❌ Error in create:', error);
    }
  }

  private setupEventListeners(): void {
    this.connector.on('puzzleSolved', () => {
      this.handleCorrectAnswer();
    });
    this.connector.on('puzzleFailed', () => {
      this.handleWrongAnswer();
    });
    this.connector.on('levelUpToMedium', () => {
      console.log('🔥 Switching to Medium Map...');
      this.switchToMap('theme_03', '1', 'tileset3', { x: 15, y: 4 });
    });
    this.connector.on('hintUsed', () => {
      this.handleHintUsed();
    });
    
  }

  private handleCorrectAnswer() {
    if (this.coinsCollected >= this.coinSprites.length) {
      this.showMessage("✅ All coins collected!");
      return;
    }
    const targetCoin = this.coinSprites[this.coinsCollected];
    console.log('🎯 Moving to coin index:', this.coinsCollected, targetCoin?.x, targetCoin?.y);
    this.movePlayerTo(targetCoin.x, targetCoin.y);
  }

  private handleWrongAnswer() {
    this.score -= 5;
    if (this.score < 0) this.score = 0;
    this.scoreText.setText(`Score: ${this.score}`);
    this.showMessage('❌ Wrong answer! -5 points');
  }

  private handleHintUsed() {
    this.score -= 1;
    if (this.score < 0) this.score = 0;
    this.scoreText.setText(`Score: ${this.score}`);
    this.showMessage('💡 Hint used! -1 point');
  }
  

  private movePlayerTo(targetX: number, targetY: number) {
    if (!this.player) return;
    this.player.play('walk-down');

    this.tweens.add({
      targets: this.player,
      x: targetX,
      y: targetY,
      duration: 800,
      onComplete: () => {
        this.player.anims.stop();
        this.player.setTexture('player', 'up/texture-7.png');
        this.checkCoinCollection();
        this.connector.emit('playerMovementComplete');
      }
    });
  }

  private checkCoinCollection() {
    this.coinGroup.getChildren().forEach((coin) => {
      const distance = Phaser.Math.Distance.Between(
        this.player.x,
        this.player.y,
        (coin as Phaser.GameObjects.Sprite).x,
        (coin as Phaser.GameObjects.Sprite).y
      );
      if (distance < 10) {
        this.collectCoin(coin);
      }
    });
  }

  private collectCoin(coin: Phaser.GameObjects.GameObject) {
    const coinSprite = coin as Phaser.GameObjects.Sprite;
    coinSprite.play('coin-disappear');
    coinSprite.once('animationcomplete', () => {
      console.log('🪙 Collected coin index:', this.coinsCollected);
      coinSprite.destroy();
      this.score += 10;
      this.scoreText.setText(`Score: ${this.score}`);
      this.showMessage('🎉 Congratulations! You got 10 points!');
      this.coinsCollected++;

      if (this.coinsCollected === 3) {
        this.showMessage('🏆 Level Completed, All Coins Collected!');

        this.unlockBadge();
        setTimeout(() => {
          this.switchToMap('theme_03', '1', 'tileset3'); 
        }, 1200); 
      }
      
    });
  }

  private showMessage(message: string) {
    this.messageText.setText(message);
    this.tweens.add({
      targets: this.messageText,
      alpha: 1,
      duration: 300,
      yoyo: true,
      hold: 1000,
      onComplete: () => {
        this.messageText.setAlpha(0);
      }
    });
  }

  private unlockBadge() {
    const badgeFrames = [1, 2, 3, 4]; // replace with actual badge frames
    if (this.currentBadgeIndex < this.badgeIcons.length) {
      const badge = this.badgeIcons[this.currentBadgeIndex];
      badge.setFrame(badgeFrames[this.currentBadgeIndex]);
      this.currentBadgeIndex++;
  
      // message for badge
      this.showMessage('🏅 You earned a new badge!');
    }
  }
  

  private switchToMap(
    mapKey: string,
    tilesetName: string,
    tilesetImageKey: string,
    playerStartPos: { x: number; y: number } = { x: 15, y: 4 } 
  ) {
    // clear the old map layers
    this.map.destroy();
  
    // reload the new map
    this.map = this.make.tilemap({ key: mapKey });
    const tileset = this.map.addTilesetImage(tilesetName, tilesetImageKey);
    if (!tileset) throw new Error(`Tileset '${tilesetName}' could not be loaded.`);
  
    // create new layers and set depth
    this.map.createLayer('ground', tileset)?.setDepth(1);
    this.map.createLayer('wall', tileset)?.setDepth(2)?.setCollisionByProperty({ collision: true });
    this.animationLayer = this.map.createLayer('animation', tileset) as Phaser.Tilemaps.TilemapLayer;
    this.animationLayer?.setDepth(3);
  
    // initialize animated tiles
    if (this.animatedTiles) {
      this.animatedTiles.init(this.map);
    }
  
    // reset player position
    this.player.setPosition(
      this.map.tileToWorldX(playerStartPos.x) ?? 0,
      this.map.tileToWorldY(playerStartPos.y) ?? 0
    );
    
  
    // place new coins
    this.coinGroup.clear(true, true);
    this.coinSprites = [];
    const newCoinPositions = [
      { x: 15, y: 10 },
      { x: 6, y: 10 },
      { x: 24, y: 10},
    ];
    newCoinPositions.forEach(pos => {
      const coin = this.add.sprite(
        this.map.tileToWorldX(pos.x) ?? 0,
        this.map.tileToWorldY(pos.y) ?? 0,
        'coin'
      );
      coin.play('coin-spin');
      coin.setDepth(11);
      this.coinGroup.add(coin);
      this.coinSprites.push(coin);
    });
  
    // reset coins collected
    this.coinsCollected = 0;
    this.showMessage('🌍 Welcome to Level 2!');
    this.scoreText.setText(`Score: ${this.score}`);
    


  }
  

  update() {}
}

export { PhaserGameScene };
